/**
 * AuthContext — JWT auth backed by httpOnly cookies (set by the backend on
 * /auth/login and /auth/refresh-token). The access token also lives in memory
 * so we can attach a Bearer header for environments where reverse proxies
 * strip cookies on cross-origin requests; we no longer persist it to
 * localStorage so XSS cannot read it.
 *
 * Bootstrap flow on page load:
 *  1. Mount → call /auth/me (browser sends the httpOnly cookie). If it
 *     succeeds, we have a session — but we don't yet have the raw token.
 *  2. Immediately call /auth/refresh-token (cookie-authenticated) to get the
 *     access token returned in the response body and stash it in memory.
 *  3. Future axios requests attach `Authorization: Bearer <in-memory token>`.
 *  4. On 401, call /auth/refresh-token again to get a fresh in-memory token.
 *  5. On logout, server clears the cookie and we drop the in-memory copy.
 *
 * Impersonation tokens stay in sessionStorage on purpose — they are
 * short-lived (typically 10–60 min), tied to a single tab, and need to
 * survive a hard refresh while impersonating.
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { prefetchLastVisited } from '../utils/routePrefetch';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const TOKEN_REFRESH_INTERVAL = 23 * 60 * 60 * 1000; // 23 hours
const IMPERSONATION_CHECK_INTERVAL = 30 * 1000;
// Skip the visibility-triggered refresh unless this much wall-clock time
// has passed since the last refresh. Stops the "everything reloads when I
// switch tabs" UX bug — 30 mins is plenty of safety margin against the
// 23-hour token lifetime while letting the user tab around freely.
const VISIBILITY_REFRESH_THROTTLE = 30 * 60 * 1000; // 30 minutes

// Always send cookies with axios calls
axios.defaults.withCredentials = true;

// In-memory holder for the access token. Stored outside React state so the
// axios request interceptor (registered once at module load) can read it
// without rebuilding on every render.
let inMemoryToken = null;
const setInMemoryToken = (v) => { inMemoryToken = v || null; };

// One-time install: attach Authorization header from the in-memory token.
// Cookies handle auth too; Bearer is the fallback for proxies that strip
// cross-origin cookies.
if (!axios.__authRequestInterceptorInstalled) {
  axios.interceptors.request.use((cfg) => {
    if (inMemoryToken && cfg && cfg.headers && !cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${inMemoryToken}`;
    }
    return cfg;
  });
  axios.__authRequestInterceptorInstalled = true;
}

// Best-effort cleanup of any tokens left over from the localStorage era
// (so reverting an old browser tab can't replay an old token).
const purgeLegacyTokens = () => {
  try {
    localStorage.removeItem('tms_auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('impersonationData');
  } catch { /* private mode etc. */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);

  const refreshTimerRef = useRef(null);
  const impersonationTimerRef = useRef(null);
  // Tracks the wall-clock time of the most recent successful token refresh.
  // Used to throttle the visibility-change handler so we don't slam
  // /auth/refresh-token (and re-render every page) every time the user
  // briefly switches tabs. Previously, EVERY tab return triggered a
  // refresh → new token value → every page's fetchData (memoized on
  // [API, token]) rebuilt → useEffect refetched → form state wiped on
  // pages like /accounting. Now we only refresh on visibility if it's
  // been more than VISIBILITY_REFRESH_THROTTLE since the last refresh.
  const lastRefreshAtRef = useRef(Date.now());

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // setToken keeps both the in-memory copy and the React state in sync
  const setToken = useCallback((next) => {
    setInMemoryToken(next);
    setTokenState(next);
  }, []);

  // 401 interceptor — silently refresh once before logging out
  useEffect(() => {
    let isRefreshing = false;

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/logout') ||
            originalRequest?.url?.includes('/auth/refresh-token') ||
            originalRequest?.url?.includes('/auth/me') ||
            originalRequest?._retry) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !isRefreshing) {
          // An admin-forced logout can't be refreshed away — bounce straight out
          // with an explanation instead of a silent, confusing sign-out.
          if (String(error.response?.data?.detail || '').includes('ended by an administrator')) {
            toast.error('An administrator ended your session. Please sign in again.');
            if (user) logout();
            return Promise.reject(error);
          }
          isRefreshing = true;
          originalRequest._retry = true;
          try {
            const res = await axios.post(`${API}/auth/refresh-token`);
            const newToken = res.data.access_token;
            setToken(newToken);
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return axios(originalRequest);
          } catch (refreshErr) {
            if (user) logout();
            return Promise.reject(refreshErr);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );

    return () => { axios.interceptors.response.eject(interceptor); };
   
  }, [API, user]);

  const refreshToken = useCallback(async () => {
    if (!user || isImpersonating) return false;
    try {
      const response = await axios.post(`${API}/auth/refresh-token`);
      setToken(response.data.access_token);
      lastRefreshAtRef.current = Date.now();
      return true;
    } catch {
      return false;
    }
  }, [user, API, isImpersonating, setToken]);

  const checkImpersonationExpiry = useCallback(() => {
    if (isImpersonating && impersonationData?.expires_at) {
      const expiresAt = new Date(impersonationData.expires_at);
      if (new Date() >= expiresAt) endImpersonation();
    }
   
  }, [isImpersonating, impersonationData]);

  // Periodic & visibility-triggered token refresh
  useEffect(() => {
    if (user && !isImpersonating) {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = setInterval(() => { refreshToken(); }, TOKEN_REFRESH_INTERVAL);

      const handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible' || !user) return;
        // Throttle: skip if we already refreshed recently. Previously every
        // tab-switch triggered a refresh and (via the new token value)
        // re-fetched every page, blowing away in-progress form input.
        const elapsed = Date.now() - lastRefreshAtRef.current;
        if (elapsed < VISIBILITY_REFRESH_THROTTLE) return;
        refreshToken().catch(() => { /* 401 interceptor will retry-then-logout */ });
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user, refreshToken, isImpersonating]);

  // Impersonation expiry checker
  useEffect(() => {
    if (isImpersonating) {
      impersonationTimerRef.current = setInterval(checkImpersonationExpiry, IMPERSONATION_CHECK_INTERVAL);
      return () => { if (impersonationTimerRef.current) clearInterval(impersonationTimerRef.current); };
    }
  }, [isImpersonating, checkImpersonationExpiry]);

  // Bootstrap on first mount: cookie -> /auth/me -> /auth/refresh-token (in-memory)
  // ref-guard prevents StrictMode double-fire and React-Fast-Refresh re-runs
  // from re-fetching auth on every render — saves ~3 round trips per page load.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    purgeLegacyTokens();

    const checkAuth = async () => {
      try {
        // Cookie should be sent automatically (withCredentials=true)
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data);

        // Get a fresh access token in memory so Bearer fallback is available
        try {
          const tokRes = await axios.post(`${API}/auth/refresh-token`);
          setToken(tokRes.data.access_token);
        } catch { /* not critical; cookie alone is enough */ }

        // Best-effort: prefetch the chunk for the user's last visited route
        // so the next navigation resolves instantly. Idle-time only.
        prefetchLastVisited();

        // Restore impersonation state if any
        try {
          const impStatus = await axios.get(`${API}/auth/impersonation-status`);
          if (impStatus.data.is_impersonating) {
            setIsImpersonating(true);
            const savedData = sessionStorage.getItem('impersonationData');
            if (savedData) setImpersonationData(JSON.parse(savedData));
            const savedOriginal = sessionStorage.getItem('originalToken');
            if (savedOriginal) {
              setOriginalToken(savedOriginal);
              try {
                const origRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedOriginal}` } });
                setOriginalUser(origRes.data);
              } catch { /* noop */ }
            }
          }
        } catch { /* impersonation check is best-effort */ }
      } catch {
        // No valid session — make sure no stale tokens are sitting around
        setToken(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, [API, setToken]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setToken(null);
    purgeLegacyTokens();
    sessionStorage.removeItem('originalToken');
    sessionStorage.removeItem('impersonationData');
    setUser(null);
    setIsImpersonating(false);
    setImpersonationData(null);
    setOriginalToken(null);
    setOriginalUser(null);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    if (impersonationTimerRef.current) clearInterval(impersonationTimerRef.current);
  }, [API, setToken]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    // Server set the httpOnly cookie; keep the token in memory for Bearer fallback
    setToken(access_token);
    setUser(userData);
    // Prefetch the chunk of the user's last visited route so Login → next page
    // navigation feels instant.
    prefetchLastVisited();
    return userData;
  }, [API, setToken]);

  const startImpersonation = useCallback(async (targetUserId, targetType = 'user') => {
    const response = await axios.post(
      `${API}/auth/impersonate`,
      { user_id: targetUserId, type: targetType }
    );
    const { impersonation_token, target_user, impersonator, expires_at, redirect_to } = response.data;

    if (targetType === 'customer') {
      sessionStorage.setItem('customerPortalToken', impersonation_token);
      sessionStorage.setItem('customerPortalUser', JSON.stringify({
        id: target_user.id, company_name: target_user.company_name, email: target_user.email,
      }));
      sessionStorage.setItem('customerPortalImpersonation', JSON.stringify({
        impersonator, target_user, expires_at, type: targetType,
      }));
      return { success: true, redirect_to: redirect_to || '/clientportal/dashboard', isCustomerPortal: true };
    }

    sessionStorage.setItem('originalToken', token || '');
    setOriginalToken(token);
    setOriginalUser(user);

    setToken(impersonation_token);
    sessionStorage.setItem('impersonationData', JSON.stringify({ impersonator, target_user, expires_at, type: targetType }));

    setIsImpersonating(true);
    setImpersonationData({ impersonator, target_user, expires_at, type: targetType });

    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${impersonation_token}` },
    });
    setUser(userResponse.data);
    return { success: true, redirect_to };
  }, [API, token, user, setToken]);

  const endImpersonation = useCallback(async () => {
    // Ask the backend to restore the admin's httpOnly access_token cookie
    // from the stashed original_access_token. Without this the browser
    // would keep the impersonation cookie and every request would still
    // resolve as the target user server-side.
    try {
      await axios.post(`${API}/auth/end-impersonation`);
    } catch { /* best-effort — proceed to clear client state anyway */ }

    const savedOriginalToken = sessionStorage.getItem('originalToken');
    sessionStorage.removeItem('originalToken');
    sessionStorage.removeItem('impersonationData');

    setToken(savedOriginalToken || null);
    setIsImpersonating(false);
    setImpersonationData(null);

    if (originalUser) {
      setUser(originalUser);
    } else {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        logout();
        return;
      }
    }

    // Grab a fresh in-memory admin token — the httpOnly cookie is already
    // restored by the backend, but Bearer fallback needs a copy too.
    try {
      const res = await axios.post(`${API}/auth/refresh-token`);
      setToken(res.data.access_token);
    } catch { /* cookie alone is enough */ }

    setOriginalToken(null);
    setOriginalUser(null);
    if (impersonationTimerRef.current) clearInterval(impersonationTimerRef.current);
  }, [API, originalUser, logout, setToken]);

  const getImpersonationTimeRemaining = useCallback(() => {
    if (!isImpersonating || !impersonationData?.expires_at) return null;
    const remainingMs = new Date(impersonationData.expires_at) - new Date();
    return remainingMs <= 0 ? 0 : Math.floor(remainingMs / 1000);
  }, [isImpersonating, impersonationData]);

  const contextValue = useMemo(() => ({
    user,
    setUser,
    token,
    login,
    logout,
    API,
    isAuthenticated: !!user,
    refreshToken,
    isImpersonating,
    impersonationData,
    startImpersonation,
    endImpersonation,
    getImpersonationTimeRemaining,
    originalUser,
  }), [
    user, token, login, logout, API, refreshToken, isImpersonating, impersonationData,
    startImpersonation, endImpersonation, getImpersonationTimeRemaining, originalUser,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
